import { cn } from '@/lib/utils'

/**
 * FenceAuth brand marks, inlined from the canonical vectors in
 * reference/asset/fenceauth-logo/svg. Inline SVG is composited as vectors at
 * every zoom level and device pixel ratio (never rasterized like <img>
 * sources can be mid-transform), loads with the bundle, and stays themeable.
 * The wordmark viewBox is cropped to the artwork — the source files carry
 * 48/40px padding — so CSS heights map 1:1 to the visible mark.
 */

const NAVY = '#1E2A5E'
const TEAL = '#14B8A6'

/* Wordmark glyph paths from the reference SVG ("fence" + "auth"). */
const FENCE_PATHS = [
  {
    d: 'M702 1118V939H20V1118ZM232 0V1246Q232 1350 278.5 1420.0Q325 1490 401.0 1525.0Q477 1560 565 1560Q632 1560 679.5 1549.0Q727 1538 749 1530L698 1352Q682 1357 658.5 1362.5Q635 1368 600 1368Q522 1368 487.0 1329.0Q452 1290 452 1216V0Z',
    x: 0,
  },
  {
    d: 'M630 -23Q465 -23 345.0 48.0Q225 119 160.5 248.0Q96 377 96 551Q96 723 159.5 854.0Q223 985 339.0 1058.5Q455 1132 611 1132Q707 1132 796.0 1100.5Q885 1069 956.0 1002.0Q1027 935 1068.5 828.5Q1110 722 1110 570V492H219V656H997L894 602Q894 705 862.0 783.5Q830 862 767.5 906.0Q705 950 611 950Q518 950 452.0 905.0Q386 860 351.5 786.5Q317 713 317 626V514Q317 399 356.5 319.5Q396 240 467.0 199.5Q538 159 632 159Q694 159 744.0 176.5Q794 194 830.5 229.5Q867 265 886 317L1093 268Q1066 182 1002.5 116.0Q939 50 844.5 13.5Q750 -23 630 -23Z',
    x: 29.593,
  },
  {
    d: 'M368 662V0H148V1118H358L360 844H337Q386 994 479.0 1063.0Q572 1132 704 1132Q818 1132 903.0 1084.0Q988 1036 1036.0 942.5Q1084 849 1084 710V0H864V683Q864 804 801.0 872.0Q738 940 628 940Q554 940 495.0 907.5Q436 875 402.0 813.0Q368 751 368 662Z',
    x: 75.41,
  },
  {
    d: 'M618 -23Q460 -23 343.0 49.0Q226 121 161.0 251.0Q96 381 96 553Q96 727 161.0 857.5Q226 988 343.0 1060.0Q460 1132 618 1132Q705 1132 781.0 1109.5Q857 1087 918.5 1044.5Q980 1002 1022.5 940.5Q1065 879 1086 802L877 752Q866 795 843.0 830.5Q820 866 787.5 892.0Q755 918 712.5 932.0Q670 946 619 946Q518 946 451.5 892.5Q385 839 352.5 750.0Q320 661 320 553Q320 446 352.5 357.5Q385 269 451.5 216.0Q518 163 619 163Q671 163 714.0 177.5Q757 192 791.0 218.5Q825 245 848.0 282.5Q871 320 881 365L1089 315Q1069 236 1026.0 173.5Q983 111 921.5 67.0Q860 23 783.0 0.0Q706 -23 618 -23Z',
    x: 122.332,
  },
  {
    d: 'M630 -23Q465 -23 345.0 48.0Q225 119 160.5 248.0Q96 377 96 551Q96 723 159.5 854.0Q223 985 339.0 1058.5Q455 1132 611 1132Q707 1132 796.0 1100.5Q885 1069 956.0 1002.0Q1027 935 1068.5 828.5Q1110 722 1110 570V492H219V656H997L894 602Q894 705 862.0 783.5Q830 862 767.5 906.0Q705 950 611 950Q518 950 452.0 905.0Q386 860 351.5 786.5Q317 713 317 626V514Q317 399 356.5 319.5Q396 240 467.0 199.5Q538 159 632 159Q694 159 744.0 176.5Q794 194 830.5 229.5Q867 265 886 317L1093 268Q1066 182 1002.5 116.0Q939 50 844.5 13.5Q750 -23 630 -23Z',
    x: 167.35,
  },
]

const AUTH_PATHS = [
  {
    d: 'M461 -25Q355 -25 269.0 14.5Q183 54 133.0 130.0Q83 206 83 316Q83 411 119.5 472.0Q156 533 218.5 569.0Q281 605 357.5 623.0Q434 641 513 650Q612 662 674.0 669.5Q736 677 765.5 694.5Q795 712 795 751V757Q795 820 771.0 863.0Q747 906 699.5 929.5Q652 953 581 953Q508 953 455.5 930.5Q403 908 370.5 873.0Q338 838 322 800L115 854Q153 954 224.0 1015.0Q295 1076 387.0 1104.0Q479 1132 577 1132Q645 1132 721.0 1116.0Q797 1100 864.0 1058.0Q931 1016 973.0 940.0Q1015 864 1015 742V0H802V153H791Q769 110 726.0 69.0Q683 28 617.5 1.5Q552 -25 461 -25ZM510 152Q600 152 664.0 187.0Q728 222 762.0 280.0Q796 338 796 403V546Q784 534 750.5 524.5Q717 515 674.0 507.5Q631 500 591.0 495.0Q551 490 525 487Q463 479 411.5 460.0Q360 441 329.5 406.0Q299 371 299 313Q299 260 326.5 224.0Q354 188 401.0 170.0Q448 152 510 152Z',
    x: 0,
  },
  {
    d: 'M529 -14Q415 -14 329.0 34.0Q243 82 195.5 176.0Q148 270 148 408V1118H368V435Q368 314 431.5 246.0Q495 178 604 178Q679 178 737.5 210.5Q796 243 830.0 305.5Q864 368 864 456V1118H1084V0H874L873 274H894Q845 122 751.0 54.0Q657 -14 529 -14Z',
    x: 44.294,
  },
  {
    d: 'M626 1118V939H20V1118ZM183 1384H403V302Q403 235 431.5 203.5Q460 172 525 172Q542 172 570.0 176.0Q598 180 618 184L657 8Q622 -4 582.0 -9.5Q542 -15 503 -15Q351 -15 267.0 63.5Q183 142 183 284Z',
    x: 91.216,
  },
  {
    d: 'M368 662V0H148V1490H366V844H337Q386 994 479.0 1063.0Q572 1132 704 1132Q818 1132 903.5 1084.0Q989 1036 1036.5 942.5Q1084 849 1084 710V0H864V683Q864 804 801.0 872.0Q738 940 628 940Q554 940 495.0 907.5Q436 875 402.0 813.0Q368 751 368 662Z',
    x: 117.762,
  },
]

function IconArtwork({ onDark }: { onDark: boolean }) {
  return (
    <>
      <rect width="96" height="96" rx="20" fill={onDark ? '#FFFFFF' : NAVY} />
      {[16, 28, 60, 72].map((x) => (
        <rect key={x} x={x} y="36" width="8" height="44" rx="4" fill={onDark ? NAVY : '#FFFFFF'} />
      ))}
      <circle cx="48" cy="32" r="10" fill={TEAL} />
    </>
  )
}

/**
 * Full horizontal lockup (icon + "fenceauth" wordmark). Tight viewBox — size
 * it by height (`h-6`, `h-7`, …) and the width follows the true aspect ratio.
 */
export function FenceAuthLogo({
  onDark = false,
  className,
}: {
  onDark?: boolean
  className?: string
}) {
  return (
    <svg
      viewBox="48 40 525.851 120"
      role="img"
      aria-label="FenceAuth"
      className={cn('h-7 w-auto', className)}
    >
      <g transform="translate(48,40) scale(1.25)">
        <IconArtwork onDark={onDark} />
      </g>
      <g transform="translate(196,128.8)">
        <g fill={onDark ? '#FFFFFF' : NAVY}>
          {FENCE_PATHS.map((glyph) => (
            <path
              key={glyph.x}
              d={glyph.d}
              transform={`translate(${glyph.x},0) scale(0.03809,-0.03809)`}
            />
          ))}
        </g>
        <g transform="translate(213.167,0)" fill={TEAL}>
          {AUTH_PATHS.map((glyph) => (
            <path
              key={glyph.x}
              d={glyph.d}
              transform={`translate(${glyph.x},0) scale(0.03809,-0.03809)`}
            />
          ))}
        </g>
      </g>
    </svg>
  )
}

/** Square icon mark alone (sidebar rail, favicon-adjacent uses). */
export function FenceAuthIcon({
  onDark = false,
  className,
}: {
  onDark?: boolean
  className?: string
}) {
  return (
    <svg viewBox="0 0 96 96" role="img" aria-label="FenceAuth" className={cn('size-7', className)}>
      <IconArtwork onDark={onDark} />
    </svg>
  )
}
